import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import MembershipPlanCard from './MembershipPlanCard';
import MembershipStatusCard from './MembershipStatusCard';
import SubscriptionTermsDisclosure from './SubscriptionTermsDisclosure';
import { useMembershipPlanPricing } from '../hooks/useMembershipPlanPricing';
import { usePayerAssignments } from '../hooks/usePayerAssignments';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { isIosNativeApp } from '../utils/nativeCheckoutBridge';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';
import {
  canShowFreeTrialChoice,
  isMembershipTrialing,
  MEMBERSHIP_PURCHASE_CHOICE_EXPLAINER,
} from '../subscriptions/membershipPurchaseUi';
import { MEMBERSHIP_PLAN_ORDER } from '../subscriptions/purchaseSteps';
import type { MembershipPlanId } from '../subscriptions/types';
import { assignmentOnboardingPath } from '../utils/onboarding';

export interface SubscriptionPurchaseOptionsProps {
  /** Post-checkout return path. Defaults to the current location pathname. */
  returnTo?: string;
}

/** Apple allows one active subscription per group per Apple Account. */
const APPLE_SECOND_SUBSCRIPTION_NOTICE =
  'Your Apple Account already has an SMSP membership. Apple allows one membership '
  + 'per account, so additional participants have to be subscribed on the website.';

const SubscriptionPurchaseOptions: React.FC<SubscriptionPurchaseOptionsProps> = ({
  returnTo: returnToProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { assignments, loading, error, refetch } = usePayerAssignments();
  const {
    confirmCheckout,
    loading: checkoutLoading,
    error: confirmError,
    clearError: clearConfirmError,
    restoreIapPurchases,
    openBillingPortal,
  } = useSubscriptionCheckout();
  const confirmAttempted = useRef<string | null>(null);

  const returnTo = returnToProp ?? location.pathname;

  const initialAssignmentId = searchParams.get('assignment_id');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | ''>('');
  const [checkoutNotice, setCheckoutNotice] = useState<{
    severity: 'success' | 'error' | 'info';
    message: string;
    instruction?: string;
    retryable?: boolean;
  } | null>(null);

  const checkoutStatus = searchParams.get('checkout');
  const sessionId = searchParams.get('session_id');

  const activeAssignmentId = useMemo(() => {
    if (selectedAssignmentId !== '') {
      return selectedAssignmentId;
    }
    if (initialAssignmentId) {
      const parsed = parseInt(initialAssignmentId, 10);
      if (!Number.isNaN(parsed) && assignments.some((a) => a.id === parsed)) {
        return parsed;
      }
    }
    return assignments.length === 1 ? assignments[0]?.id : undefined;
  }, [assignments, initialAssignmentId, selectedAssignmentId]);

  const activeAssignment = assignments.find((a) => a.id === activeAssignmentId);
  const entitlements = normalizeEntitlementsMap(activeAssignment?.entitlements);
  const membership = activeAssignment?.membership ?? null;
  const hasLiveMembership =
    membership?.status === 'active' || membership?.status === 'trialing';

  // Apple refuses a second subscription in the same group, so the plan cards
  // would fail at the StoreKit sheet rather than here. Say so instead.
  const appleSubscriptionElsewhere =
    isIosNativeApp()
    && !hasLiveMembership
    && assignments.some(
      (assignment) =>
        assignment.id !== activeAssignmentId
        && assignment.membership?.provider === 'apple_iap'
        && (assignment.membership?.status === 'active'
          || assignment.membership?.status === 'trialing'),
    );

  const clearCheckoutQuery = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    next.delete('return_to');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const runConfirm = useCallback(
    async (stripeSessionId: string) => {
      clearConfirmError();
      const result = await confirmCheckout(stripeSessionId);
      clearCheckoutQuery();

      if (result) {
        await refetch();
        setCheckoutNotice({
          severity: 'success',
          message: result.alreadyFulfilled
            ? `${result.productName} is already active on your assignment.`
            : `You're subscribed — ${result.productName} is now active.`,
        });
        // Assignment-less checkouts create a brand-new assignment; route into
        // onboarding so setup can continue from whatever page hosted checkout.
        navigate(assignmentOnboardingPath(result.assignmentId), {
          replace: true,
          state: {
            checkoutSuccess: result.productName,
            alreadyFulfilled: result.alreadyFulfilled,
          },
        });
      }
    },
    [clearCheckoutQuery, clearConfirmError, confirmCheckout, navigate, refetch],
  );

  useEffect(() => {
    if (checkoutStatus === 'cancelled') {
      setCheckoutNotice({
        severity: 'info',
        message: 'Checkout was cancelled.',
      });
      clearCheckoutQuery();
    }
  }, [checkoutStatus, clearCheckoutQuery]);

  useEffect(() => {
    if (checkoutStatus !== 'success' || !sessionId || confirmAttempted.current === sessionId) {
      return;
    }
    confirmAttempted.current = sessionId;
    void runConfirm(sessionId);
  }, [checkoutStatus, sessionId, runConfirm]);

  useEffect(() => {
    if (confirmError) {
      setCheckoutNotice({
        severity: 'error',
        message: confirmError.message,
        instruction: confirmError.instruction,
        retryable: confirmError.retryable,
      });
    }
  }, [confirmError]);

  const handleRetryConfirm = () => {
    if (!sessionId) return;
    setCheckoutNotice(null);
    confirmAttempted.current = null;
    void runConfirm(sessionId);
  };

  const handleRestorePurchases = async () => {
    clearConfirmError();
    const restored = await restoreIapPurchases();
    if (!restored.length) {
      setCheckoutNotice({
        severity: 'info',
        message: 'No purchases were found to restore.',
      });
      return;
    }
    await refetch();
    const names = restored.map((item) => item.productName).join(', ');
    setCheckoutNotice({
      severity: 'success',
      message: restored.every((item) => item.alreadyFulfilled)
        ? `Restored purchases: ${names} (already active).`
        : `Restored purchases: ${names}.`,
    });
  };

  const handleManageBilling = async () => {
    clearConfirmError();
    const url = await openBillingPortal(activeAssignmentId);
    if (url) {
      window.location.assign(url);
    }
  };

  const planDisabledReason = (): string | undefined => {
    if (appleSubscriptionElsewhere) {
      return APPLE_SECOND_SUBSCRIPTION_NOTICE;
    }
    if (membership?.status === 'trialing' && !membership?.org_provided) {
      return undefined;
    }
    if (hasLiveMembership) {
      return membership?.org_provided
        ? 'Your organization provides this membership.'
        : 'This assignment already has an active membership.';
    }
    return undefined;
  };

  const disabledReason = planDisabledReason();
  const planPricing = useMembershipPlanPricing();
  const trialLabel = planPricing.membership_monthly.trialLabel;
  const trialChoice = canShowFreeTrialChoice(membership, trialLabel);
  const trialing = isMembershipTrialing(membership);
  const showPurchaseLegalFooter = !loading && !disabledReason && (!hasLiveMembership || trialing);

  return (
    <Box data-testid="subscription-purchase-options">
      {isIosNativeApp() && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            onClick={() => void handleRestorePurchases()}
            disabled={checkoutLoading}
            data-testid="restore-iap-purchases"
          >
            Restore Purchases
          </Button>
        </Box>
      )}
      {(checkoutLoading || (checkoutStatus === 'success' && sessionId)) && (
        <Box sx={{ mb: 1.5 }} data-testid="checkout-confirming">
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Confirming your subscription…
          </Typography>
        </Box>
      )}

      {checkoutNotice && (
        <Alert
          severity={checkoutNotice.severity}
          sx={{ mb: 1.5, py: 0 }}
          data-testid="checkout-notice"
          data-attr={`checkout-notice--${checkoutNotice.severity}`}
          action={
            checkoutNotice.retryable && sessionId ? (
              <Button color="inherit" size="small" onClick={handleRetryConfirm}>
                Try again
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">{checkoutNotice.message}</Typography>
          {checkoutNotice.instruction && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {checkoutNotice.instruction}
            </Typography>
          )}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
          {error}
        </Alert>
      )}

      {!loading && assignments.length > 1 && (
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id="subscription-assignment-select">Subscribe for participant</InputLabel>
          <Select
            labelId="subscription-assignment-select"
            label="Subscribe for participant"
            value={activeAssignmentId ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedAssignmentId(value === '' ? '' : Number(value));
            }}
          >
            <MenuItem value="">
              <em>New participant</em>
            </MenuItem>
            {assignments.map((assignment) => (
              <MenuItem key={assignment.id} value={assignment.id}>
                Assignment #{assignment.id}
                {assignment.athlete?.str ? ` — ${assignment.athlete.str}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!loading && assignments.length === 1 && activeAssignment && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Subscribing for assignment #{activeAssignment.id}
          {activeAssignment.athlete?.str ? ` (${activeAssignment.athlete.str})` : ''}.
        </Typography>
      )}

      {!loading && assignments.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Subscribing creates a new participant you can send invites for afterwards.
        </Typography>
      )}

      {!loading && <MembershipStatusCard membership={membership} />}

      {!loading && hasLiveMembership && !membership?.org_provided && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleManageBilling()}
            disabled={checkoutLoading || membership?.provider === 'apple_iap'}
            data-attr="subscription_manage-billing"
            data-testid="manage-billing"
          >
            Manage billing
          </Button>
          {membership?.provider === 'apple_iap' && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Billed by Apple — manage this membership in your Apple Account settings.
            </Typography>
          )}
        </Box>
      )}

      {!loading && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
          data-testid="membership-plan-picker"
        >
          {MEMBERSHIP_PLAN_ORDER.map((planId: MembershipPlanId) => (
            <MembershipPlanCard
              key={planId}
              planId={planId}
              assignmentId={activeAssignmentId}
              entitlements={entitlements}
              membership={membership}
              returnTo={returnTo}
              disabled={checkoutLoading || Boolean(disabledReason)}
              {...(disabledReason ? { disabledReason } : {})}
              emphasized={planId === 'membership_annual'}
              {...(planId === 'membership_annual' ? { badge: 'Best value' } : {})}
            />
          ))}
        </Box>
      )}

      {showPurchaseLegalFooter && (
        <Box sx={{ mt: 2 }} data-testid="subscription-purchase-legal-footer">
          {trialChoice && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {MEMBERSHIP_PURCHASE_CHOICE_EXPLAINER}
            </Typography>
          )}
          <SubscriptionTermsDisclosure
            {...(trialChoice && trialLabel ? { trialLabel } : {})}
            compact
          />
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionPurchaseOptions;
